import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, MenuItem, IconButton, FormControl, InputLabel, Select, Chip, TablePagination } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { Dialog as MuiDialog } from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const DepositOrdersAdmin = () => {
  const { user } = useContext(AuthContext);
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Check if current user is admin (to show filter) or merchant (to auto-filter)
  const isAdmin = currentUser?.role === 'admin';
  const isMerchant = currentUser?.role === 'merchant';
  const userMerchantId = currentUser?.merchant_id;

  const [openDialog, setOpenDialog] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form state
  const [appUserId, setAppUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [paymentMethodName, setPaymentMethodName] = useState('');
  const [status, setStatus] = useState('pending');
  const [userRemarks, setUserRemarks] = useState('');
  const [adminRemarks, setAdminRemarks] = useState('');
  const [transactionReference, setTransactionReference] = useState('');
  
  const [appUsers, setAppUsers] = useState([]);
  const [paymentMethodsList, setPaymentMethodsList] = useState([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  
  const fetchMerchants = async () => {
    try {
      const response = await axios.get('/admin/merchants');
      setMerchants(response.data.merchants || []);
    } catch (err) {
      console.error('Error fetching merchants:', err);
    }
  };

  const fetchAppUsers = async () => {
    try {
      let endpoint = '/admin/app-users-list';
      
      // For merchant users, fetch only their merchant's app users
      if (isMerchant && userMerchantId) {
        endpoint = `/admin/app-users?merchant_id=${userMerchantId}`;
      }
      
      const response = await axios.get(endpoint); 
      if (response.data && response.data.success) {
        // Handle different response structures
        const users = response.data.users || response.data.appUsers || [];
        setAppUsers(users);
      } else {
        console.warn('Could not fetch app users for dropdown:', response.data.message)
        setAppUsers([]);
      }
    } catch (err) {
      console.error("Error fetching app users:", err);
      setError('Could not load app users for selection.');
      setAppUsers([]);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get('/payment-methods');
      if (response.data && response.data.success && response.data.data) {
        setPaymentMethodsList(response.data.data);
      } else {
        console.warn('Could not fetch payment methods:', response.data.message);
        setPaymentMethodsList([]);
      }
    } catch (err) {
      console.error("Error fetching payment methods:", err);
      setPaymentMethodsList([]);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/deposit-orders/all');
      if (response.data && response.data.success) {
        const ordersData = response.data.orders;
        setOrders(ordersData);
        
        // Auto-filter for merchants
        if (isMerchant && userMerchantId) {
          const merchantOrders = ordersData.filter(order => order.merchant_id === userMerchantId);
          setFilteredOrders(merchantOrders);
        } else {
          setFilteredOrders(ordersData);
        }
      } else {
        setError(response.data.message || 'Failed to fetch deposit orders.');
      }
    } catch (err) {
      console.error("Error fetching deposit orders:", err);
      setError(err.response?.data?.message || err.message || 'An error occurred while fetching deposit orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchMerchants(); // Only fetch merchants for admin
    }
    fetchOrders();
    fetchAppUsers();
    fetchPaymentMethods(); // Fetch payment methods on component mount
  }, [currentUser]);

  // Filter orders by merchant (only for admin)
  useEffect(() => {
    if (isAdmin) {
      if (selectedMerchant === '') {
        setFilteredOrders(orders);
      } else {
        const filtered = orders.filter(order => 
          order.merchant_id === selectedMerchant
        );
        setFilteredOrders(filtered);
      }
    }
  }, [selectedMerchant, orders, isAdmin]);

  const handleMerchantFilter = (event) => {
    setSelectedMerchant(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const clearFilter = () => {
    setSelectedMerchant('');
    setPage(0); // Reset to first page when clearing filter
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getMerchantName = (merchant_id, merchant_name) => {
    // Use merchant_name from the API response if available
    if (merchant_name) {
      return merchant_name;
    }
    // Fallback to looking up in merchants array (for backward compatibility)
    const merchant = merchants.find(m => m.merchant_id === merchant_id);
    return merchant ? merchant.name : 'Unknown Merchant';
  };

  const handleOpenDialog = (order = null) => {
    if (order) {
      setIsEditMode(true);
      setCurrentOrder(order);
      setAppUserId(order.app_user_id);
      setAmount(order.amount.toString());
      setPaymentMethodId(order.payment_method_id?.toString() || '');
      setPaymentMethodName(order.payment_method_name || '');
      setStatus(order.status);
      setUserRemarks(order.user_remarks || '');
      setAdminRemarks(order.admin_remarks || '');
      setTransactionReference(order.transaction_reference || '');
    } else {
      setIsEditMode(false);
      setCurrentOrder(null);
      setAppUserId('');
      setAmount('');
      setPaymentMethodId('');
      setPaymentMethodName('');
      setStatus('pending');
      setUserRemarks('');
      setAdminRemarks('');
      setTransactionReference('');
    }
    setOpenDialog(true);
  };

  const handlePaymentMethodChange = (selectedId) => {
    const selectedMethod = paymentMethodsList.find(method => method.id.toString() === selectedId);
    if (selectedMethod) {
      setPaymentMethodId(selectedId);
      setPaymentMethodName(selectedMethod.name);
    } else {
      setPaymentMethodId(selectedId);
      setPaymentMethodName('');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentOrder(null);
  };

  const handleSubmit = async () => {
    if (!appUserId || !amount || !paymentMethodId || !paymentMethodName || !status) {
        alert("App User, Amount, Payment Method ID, Payment Method Name, and Status are required.");
        return;
    }
    if (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        alert("Invalid amount. Amount must be a positive number.");
        return;
    }

    const orderData = {
      app_user_id: parseInt(appUserId, 10),
      amount: parseFloat(amount),
      payment_method_id: parseInt(paymentMethodId, 10),
      payment_method_name: paymentMethodName,
      status: status,
      user_remarks: userRemarks,
      admin_remarks: adminRemarks,
      transaction_reference: transactionReference,
    };

    try {
      let response;
      if (isEditMode && currentOrder) {
        // Use the correct endpoint for updating orders
        response = await axios.put(`/deposit-orders/all/${currentOrder.id}`, orderData);
      } else {
        response = await axios.post('/deposit-orders/admin/create', orderData);
      }
      if (response.data && response.data.success){
        fetchOrders();
        handleCloseDialog();
      } else {
        alert(response.data.message || 'An error occurred while saving the order.');
      }
    } catch (err) {
      console.error("Error saving deposit order:", err);
      alert(err.response?.data?.message || err.message || 'Failed to save deposit order.');
    }
  };

  const handleDelete = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this deposit order?')) {
      try {
        const response = await axios.delete(`/deposit-orders/admin/delete/${orderId}`);
        if (response.data && response.data.success){
            fetchOrders();
        } else {
            alert(response.data.message || 'An error occurred while deleting the order.');
        }
      } catch (err) {
        console.error("Error deleting deposit order:", err);
        alert(err.response?.data?.message || err.message || 'Failed to delete deposit order.');
      }
    }
  };

  if (loading && orders.length === 0) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Deposit Orders Management {isMerchant && currentUser?.merchant_name ? `(${currentUser.merchant_name})` : ''}
        </Typography>
        {/* Show Create button for both admin and merchant users */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create New Deposit Order
        </Button>
      </Box>

      {error && !openDialog && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filter Section - Only show for admin */}
      {isAdmin && (
        <Box display="flex" alignItems="center" gap={2} mb={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <FilterListIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Merchant</InputLabel>
            <Select
              value={selectedMerchant}
              label="Filter by Merchant"
              onChange={handleMerchantFilter}
            >
              <MenuItem value="">All Merchants</MenuItem>
              {merchants.map((merchant) => (
                <MenuItem key={merchant.merchant_id} value={merchant.merchant_id}>
                  {merchant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedMerchant && (
            <Chip 
              label={`Filtered: ${getMerchantName(selectedMerchant, null)}`}
              onDelete={clearFilter}
              color="primary"
              variant="outlined"
            />
          )}
          <Typography variant="body2" color="textSecondary">
            Showing {filteredOrders.length} of {orders.length} orders
          </Typography>
        </Box>
      )}

      {/* Show count info for merchants */}
      {isMerchant && (
        <Box mb={3} p={2} sx={{ backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Showing {filteredOrders.length} deposit orders for your merchant account
          </Typography>
        </Box>
      )}

      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User (ID/Email)</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tx Ref</TableCell>
                <TableCell>Payment Slip</TableCell>
                {/* Only show Merchant column for admin */}
                {isAdmin && <TableCell>Merchant</TableCell>}
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrders.length === 0 && !loading && (
                <TableRow>
                    <TableCell colSpan={isAdmin ? 10 : 9} align="center">No deposit orders found.</TableCell>
                </TableRow>
              )}
              {paginatedOrders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>{order.id}</TableCell>
                  <TableCell>{order.app_user_username || 'N/A'} ({order.app_user_id}) <br/> <small>{order.app_user_email || 'N/A'}</small></TableCell>
                  <TableCell>{order.amount}</TableCell>
                  <TableCell>{order.payment_method_name}</TableCell>
                  <TableCell>{order.status}</TableCell>
                  <TableCell>{order.transaction_reference || 'N/A'}</TableCell>
                  <TableCell>
                    {order.payment_slip_url ? (
                      <img
                        src={order.payment_slip_url}
                        alt="Payment Slip"
                        style={{maxWidth: '100px', maxHeight: '100px', cursor: 'pointer'}} 
                        onClick={() => setSelectedImageUrl(order.payment_slip_url)}
                      />
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  {/* Only show Merchant cell for admin */}
                  {isAdmin && (
                    <TableCell>
                      <Chip 
                        label={getMerchantName(order.merchant_id, order.merchant_name)}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  <TableCell>{new Date(order.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    {/* Show edit button for both admin and merchant users */}
                    <IconButton onClick={() => handleOpenDialog(order)} size="small" title="Edit">
                      <EditIcon />
                    </IconButton>
                    {/* Only show delete button for admin users */}
                    {isAdmin && (
                      <IconButton onClick={() => handleDelete(order.id)} size="small" title="Delete">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Deposit Order' : 'Create New Deposit Order'}</DialogTitle>
        <DialogContent>
          {isEditMode && currentOrder && currentOrder.payment_slip_url && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2" gutterBottom>Payment Slip:</Typography>
              <img
                src={currentOrder.payment_slip_url}
                alt="Payment Slip Preview"
                style={{ maxWidth: '150px', maxHeight: '150px', cursor: 'pointer', border: '1px solid #eee', borderRadius: 4 }}
                onClick={() => setSelectedImageUrl(currentOrder.payment_slip_url)}
              />
            </Box>
          )}
          <TextField
            select
            label="App User"
            value={appUserId}
            onChange={(e) => setAppUserId(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={isEditMode || appUsers.length === 0} 
          >
            <MenuItem value=""><em>{appUsers.length === 0 ? (loading ? 'Loading users...' : 'No users available') : 'Select User'}</em></MenuItem>
            {appUsers.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.username} ({user.email})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            margin="normal"
            required
            InputProps={{ inputProps: { min: 0.01, step: 0.01 } }}
          />
          <TextField
            select
            label="Payment Method"
            value={paymentMethodId}
            onChange={(e) => handlePaymentMethodChange(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={paymentMethodsList.length === 0}
          >
            <MenuItem value="">
              <em>{paymentMethodsList.length === 0 ? (loading ? 'Loading methods...' : 'No methods available') : 'Select Payment Method'}</em>
            </MenuItem>
            {paymentMethodsList.map((method) => (
              <MenuItem key={method.id} value={method.id.toString()}>
                {method.name} (ID: {method.id})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Payment Method Name"
            value={paymentMethodName}
            onChange={(e) => setPaymentMethodName(e.target.value)}
            fullWidth
            margin="normal"
            required
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            fullWidth
            margin="normal"
            required
          >
            {['pending', 'completed', 'failed', 'cancelled', 'processing'].map((s) => (
              <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="User Remarks"
            value={userRemarks}
            onChange={(e) => setUserRemarks(e.target.value)}
            fullWidth
            multiline
            rows={2}
            margin="normal"
          />
          <TextField
            label="Admin Remarks"
            value={adminRemarks}
            onChange={(e) => setAdminRemarks(e.target.value)}
            fullWidth
            multiline
            rows={2}
            margin="normal"
          />
          <TextField
            label="Transaction Reference"
            value={transactionReference}
            onChange={(e) => setTransactionReference(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditMode ? 'Save Changes' : 'Create Order'}
          </Button>
        </DialogActions>
      </Dialog>

      <MuiDialog open={!!selectedImageUrl} onClose={() => setSelectedImageUrl(null)} maxWidth="md">
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img src={selectedImageUrl} alt="Payment Slip Large" style={{maxWidth: '90vw', maxHeight: '80vh'}} />
          <Button onClick={() => setSelectedImageUrl(null)} sx={{ mt: 2 }}>Close</Button>
        </Box>
      </MuiDialog>
    </Box>
  );
};

export default DepositOrdersAdmin;
